import CoursePretestButton from "../../shared/pretests/CoursePretestButton";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Alert, Spinner } from "react-bootstrap";
import CourseAccessGate from "../../shared/components/CourseAccessGate";
import { BsChevronRight, BsChevronDown } from "react-icons/bs";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../shared/libs/firebaseConfig";
import { useAuth } from "../../shared/auth/AuthProvider";
import { AFFILIATE_COLLECTIONS, AFFILIATE_STATUSES } from "../../shared/utils/affiliateSchema";
import {
  buildAffiliateAttribution,
  clearAffiliateAttributionForCourse,
  saveAffiliateAttribution,
} from "../../shared/utils/affiliateAttribution";

import "./CourseDetailPage.css";
import chicchic_logo_header01 from "../../assets/chicchic_login_logo.png";
import chicchic_logo_darkmode from "../../assets/chicchic_logo_darkmode.png";
import InstallmentCourseEntryGuard from "../../shared/components/InstallmentCourseEntryGuard";

// ✅ แปลงลิงก์ youtube ให้เป็น videoId
function extractYouTubeId(url = "") {
  const u = String(url || "").trim();
  if (!u) return "";
  try {
    const m1 = u.match(/youtu\.be\/([a-zA-Z0-9_-]{6,})/);
    if (m1?.[1]) return m1[1];

    const m2 = u.match(/[?&]v=([a-zA-Z0-9_-]{6,})/);
    if (m2?.[1]) return m2[1];

    const m3 = u.match(/shorts\/([a-zA-Z0-9_-]{6,})/);
    if (m3?.[1]) return m3[1];

    const m4 = u.match(/embed\/([a-zA-Z0-9_-]{6,})/);
    if (m4?.[1]) return m4[1];
  } catch {}
  return "";
}

function safeArray(v) {
  return Array.isArray(v) ? v : [];
}

function toDateInputValue(value) {
  try {
    if (!value) return "";

    if (typeof value === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    if (value?.toDate) {
      const d = value.toDate();
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }

    if (value instanceof Date) {
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, "0");
      const day = String(value.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  } catch {}

  return "";
}

function formatThaiFullDate(value) {
  try {
    if (!value) return "";

    let d = null;

    if (typeof value === "string") {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        d = new Date(`${value}T00:00:00`);
      } else {
        d = new Date(value);
      }
    } else if (value?.toDate) {
      d = value.toDate();
    } else if (value instanceof Date) {
      d = value;
    }

    if (!d || Number.isNaN(d.getTime())) return "";

    /* return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }); */
    return d.toLocaleDateString("th-TH", {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return "";
  }
}

function isVideoReady(video = {}) {
  return !!video?.isReady || !!video?.readyToStream || video?.status === "ready";
}

function getLessonBulletStyle(status) {
  switch (status) {
    case "published":
      return {
        background: "#198754",
        boxShadow: "0 0 0 6px rgba(25,135,84,.12)",
      };
    case "scheduled":
      return {
        background: "#fd7e14",
        boxShadow: "0 0 0 6px rgba(253,126,20,.14)",
      };
    default:
      return {
        background: "#adb5bd",
        boxShadow: "0 0 0 6px rgba(173,181,189,.16)",
      };
  }
}

function formatCoursePrice(course) {
  if (!course) return "—";
  if (course?.courseType === "thaibar") return "ราคาตามแพ็คเกจ";
  if (course?.priceText) return `฿${String(course.priceText)}`;

  const n = typeof course?.price === "number" ? course.price : Number(course?.price);
  if (!Number.isFinite(n)) return "—";
  return `฿${n.toLocaleString("th-TH")}`;
}

function isActiveAffiliateCode(data = {}) {
  return data?.status === AFFILIATE_STATUSES.approved || data?.status === "active";
}

export default function CourseDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const { user } = useAuth();

  const [course, setCourse] = useState(null);
  const [headers, setHeaders] = useState([]);

  const [loading, setLoading] = useState(true);
  const [loadingStructure, setLoadingStructure] = useState(false);
  const [loadingEnroll, setLoadingEnroll] = useState(false);
  const [err, setErr] = useState("");

  const [myGrantedEnrolls, setMyGrantedEnrolls] = useState([]);

  const defaultInstructor = {
    name: "ทีมติวเตอร์ ChicChic",
    bio: "ติวเตอร์มืออาชีพด้านกฎหมายและการสอนออนไลน์",
    photo: chicchic_logo_darkmode,
  };

  const youtubeVideoId = useMemo(() => {
    return extractYouTubeId(course?.videoAdsUrl || "");
  }, [course?.videoAdsUrl]);

/*   const [openHeaderKey, setOpenHeaderKey] = useState("0");
  const [openSectionKey, setOpenSectionKey] = useState("0-0"); */
  const [openHeaderKey, setOpenHeaderKey] = useState("");
  const [openSectionKey, setOpenSectionKey] = useState("");
  const [curriculumExpandedAll, setCurriculumExpandedAll] = useState(false);

  const curriculumData = useMemo(() => {
    return safeArray(headers).map((h) => ({
      headerTitle: h?.title || "Header",
      headerType: h?.headerType || "",
      sections: safeArray(h?.sections).map((s) => ({
        title: s?.title || "Section",
        topics: safeArray(s?.items).map((it) => {
          const video = it?.video || {};
          const ready = isVideoReady(video);
          const published = it?.contentStatus === "published";

          let bulletStatus = "empty";
          if (ready && published) {
            bulletStatus = "published";
          } else if (ready && !published) {
            bulletStatus = "scheduled";
          }

          return {
            title: it?.title || "-",
            releaseDateText: formatThaiFullDate(
              it?.releaseDateText || it?.releaseAt || toDateInputValue(it?.releaseAt) || ""
            ),
            bulletStatus,
          };
        }),
      })),
    }));
  }, [headers]);

  const hasGrantedAccess = myGrantedEnrolls.length > 0;

  const courseForEntryGuard = useMemo(() => {
    if (!course?.id) return null;

    return {
      id: course.id,
      enrolls: Array.isArray(myGrantedEnrolls) ? myGrantedEnrolls : [],
    };
  }, [course, myGrantedEnrolls]);

  useEffect(() => {
    if (!slug) return;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const q = query(
          collection(db, "courses"),
          where("slug", "==", slug),
          limit(1)
        );

        const snap = await getDocs(q);

        if (snap.empty) {
          setErr("ไม่พบคอร์สนี้");
          setCourse(null);
          setLoading(false);
          return;
        }

        const doc0 = snap.docs[0];
        const data = doc0.data();

        setCourse({ id: doc0.id, ...data });
        setLoading(false);
      } catch (e) {
        console.error(e);
        setErr(e?.message || "โหลดข้อมูลคอร์สไม่สำเร็จ");
        setLoading(false);
      }
    })();
  }, [slug]);

  useEffect(() => {
    if (!course?.id) return;

    (async () => {
      try {
        setLoadingStructure(true);

        const headerSnap = await getDocs(
          query(
            collection(db, "courses", course.id, "headers"),
            orderBy("order", "asc")
          )
        );

        const nextHeaders = [];

        for (const h of headerSnap.docs) {
          const headerData = { id: h.id, ...h.data(), sections: [] };

          const sectionSnap = await getDocs(
            query(
              collection(db, "courses", course.id, "headers", h.id, "sections"),
              orderBy("order", "asc")
            )
          );

          const nextSections = [];

          for (const s of sectionSnap.docs) {
            const sectionData = { id: s.id, ...s.data(), items: [] };

            const itemSnap = await getDocs(
              query(
                collection(
                  db,
                  "courses",
                  course.id,
                  "headers",
                  h.id,
                  "sections",
                  s.id,
                  "items"
                ),
                orderBy("order", "asc")
              )
            );

            sectionData.items = itemSnap.docs.map((x) => ({
              id: x.id,
              ...x.data(),
            }));

            nextSections.push(sectionData);
          }

          headerData.sections = nextSections;
          nextHeaders.push(headerData);
        }

        setHeaders(nextHeaders);
        setLoadingStructure(false);
      } catch (e) {
        console.error(e);
        setLoadingStructure(false);
      }
    })();
  }, [course?.id]);

  /* useEffect(() => {
    if (!course?.id) return;
    setOpenHeaderKey("0");
    setOpenSectionKey("0-0");
  }, [course?.id]); */
  useEffect(() => {
    if (!course?.id) return;
    setOpenHeaderKey("");
    setOpenSectionKey("");
    setCurriculumExpandedAll(false);
  }, [course?.id]);

  useEffect(() => {
    if (!user?.uid || !course?.id) {
      setMyGrantedEnrolls([]);
      return;
    }

    (async () => {
      try {
        setLoadingEnroll(true);

        const q = query(
          collection(db, "enrolls"),
          where("userId", "==", user.uid),
          where("courseId", "==", course.id)
        );

        const snap = await getDocs(q);

        const granted = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((x) => x?.accessStatus === "granted");

        setMyGrantedEnrolls(granted);
        setLoadingEnroll(false);
      } catch (e) {
        console.error(e);
        setLoadingEnroll(false);
      }
    })();
  }, [user?.uid, course?.id]);

  useEffect(() => {
    if (!course?.id) return;

    const refCode = new URLSearchParams(location.search).get("ref")?.trim();
    if (!refCode) {
      clearAffiliateAttributionForCourse(course.id);
      return;
    }

    let cancelled = false;

    const captureAffiliateRef = async () => {
      try {
        if (course?.affiliateEnabled !== true) {
          clearAffiliateAttributionForCourse(course.id);
          return;
        }

        const codeSnap = await getDoc(doc(db, AFFILIATE_COLLECTIONS.codes, refCode));

        if (cancelled) return;

        if (!codeSnap.exists()) {
          clearAffiliateAttributionForCourse(course.id);
          return;
        }

        const codeData = codeSnap.data();
        if (!isActiveAffiliateCode(codeData) || !codeData?.affiliateUserId) {
          clearAffiliateAttributionForCourse(course.id);
          return;
        }

        saveAffiliateAttribution(
          buildAffiliateAttribution({
            code: refCode,
            affiliateId: codeData?.affiliateId || codeData?.affiliateUserId || "",
            affiliateUserId: codeData.affiliateUserId,
            courseId: course.id,
            courseSlug: course.slug || slug || "",
          })
        );
      } catch (e) {
        console.warn("affiliate attribution capture failed:", e?.message);
      }
    };

    captureAffiliateRef();

    return () => {
      cancelled = true;
    };
  }, [course?.id, course?.affiliateEnabled, course?.slug, location.search, slug]);

  const goBackSmart = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/courses");
    }
  };

  const openHeaderAndSection = (hi, si) => {
    setOpenHeaderKey(String(hi));
    setOpenSectionKey(`${hi}-${si}`);
  };

  const toggleExpandAllCurriculum = () => {
    if (curriculumExpandedAll) {
      setOpenHeaderKey("");
      setOpenSectionKey("");
      setCurriculumExpandedAll(false);
      return;
    }

    setOpenHeaderKey("__all__");
    setOpenSectionKey("__all__");
    setCurriculumExpandedAll(true);
  };

  const onEnroll = () => {
    if (!course?.id) return;

    if (!user?.uid) {
      navigate("/login", {
        state: { from: location },
      });
      return;
    }

    navigate(`/courses/${course.id}/enroll`);
  };


  if (loading) {
    return (
      <div className="container py-5 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลด…
      </div>
    );
  }

  if (err) {
    return (
      <div className="container py-4">
        <Alert variant="danger">{err}</Alert>
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none"
          onClick={goBackSmart}
        >
          ← กลับไปหน้าก่อนหน้า
        </button>
      </div>
    );
  }

  if (!course) return null;

  const highlights = safeArray(course.courseHighlights);
  const receives = safeArray(course.courseReceive);
  const instructor = course?.instructor || defaultInstructor;

  return (
    <div className="container py-4">
      <div className="mb-3">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none"
          onClick={goBackSmart}
        >
          ← กลับไปหน้าก่อนหน้า
        </button>
      </div>

      <div className="card app-card course-detail-shell ">
        <div className="card-body">
          <div className="row g-4">
            <div className="col-lg-8 order-2 order-lg-1">
              <h1 className="h3 mb-2">{course.title}</h1>
              <p className="text-secondary mb-3">{course.subTitle}</p>

              <div className="d-flex flex-wrap gap-2 mb-3">
                <span className="badge course-detail-badge">
                  อัปเดตล่าสุด:{" "}
                  {course.updatedAt?.toDate
                    ? course.updatedAt.toDate().toLocaleDateString("th-TH", {
                        year: "numeric",
                        month: "short",
                      })
                    : "-"}
                </span>

                {Array.isArray(course.categories) && course.categories.length > 0 && (
                  <span className="badge course-detail-badge">
                    หมวด: {course.categories.join(", ")}
                  </span>
                )}
              </div>

              {highlights.length > 0 && (
                <>
                  <h2 className="h5 mt-4">ไฮไลต์</h2>
                  <ul className="mb-4">
                    {highlights.map((t, i) => (
                      <li key={i}>{t}</li>
                    ))}
                  </ul>
                </>
              )}

              <h2 className="h5">คำอธิบายคอร์ส</h2>
              <p className="text-secondary">{course.courseDescriptions || "-"}</p>

              <div className="d-flex align-items-center justify-content-between gap-2 mt-4 mb-2">
                <h2 className="h5 mb-0">โครงบทเรียน (ตัวอย่าง)</h2>

                {curriculumData.length > 0 ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={toggleExpandAllCurriculum}
                  >
                    {curriculumExpandedAll ? "ย่อทั้งหมด" : "แสดงทั้งหมด"}
                  </button>
                ) : null}
              </div>

              {loadingStructure ? (
                <div className="text-secondary">
                  <Spinner animation="border" size="sm" /> กำลังโหลดโครงบทเรียน...
                </div>
              ) : curriculumData.length === 0 ? (
                <div className="text-secondary">ยังไม่มีโครงสร้างการเรียน</div>
              ) : (
                <div className="cc-curr">
                  {curriculumData.map((h, hi) => {
                    const headerKey = String(hi);
                    const headerOpen = curriculumExpandedAll || openHeaderKey === headerKey;

                    return (
                      <div key={headerKey}>
                        <button
                          type="button"
                          className={`cc-row ${headerOpen ? "is-open" : ""}`}
                          onClick={() => {
                            setCurriculumExpandedAll(false);

                            if (headerOpen && !curriculumExpandedAll) {
                              setOpenHeaderKey("");
                            } else {
                              setOpenHeaderKey(headerKey);
                              setOpenSectionKey(`${hi}-0`);
                            }
                          }}
                        >
                          <div>
                            <div className="cc-title">
                              {/* {hi + 1}. {h?.headerTitle || "Header"} */}
                              {h?.headerTitle || "Header"}
                            </div>
                            <div className="cc-sub">
                              {safeArray(h?.sections).length} หัวข้อบทเรียน
                            </div>
                          </div>

                          <div className={`cc-chevron ${headerOpen ? "is-open" : "is-closed"}`}>
                            {headerOpen ? <BsChevronDown /> : <BsChevronRight />}
                          </div>
                        </button>

                        <div className="cc-divider" />

                        {/* {!headerOpen && safeArray(h?.sections).length > 0 && (
                          <div className="cc-preview">
                            {safeArray(h.sections).map((s, si) => {
                              const secKey = `${hi}-${si}`;
                              const isActive = openSectionKey === secKey;

                              return (
                                <div
                                  key={`${secKey}-preview`}
                                  className={`cc-prev-item ${isActive ? "is-active" : ""}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => openHeaderAndSection(hi, si)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      openHeaderAndSection(hi, si);
                                    }
                                  }}
                                >
                                  <div>
                                    <div className="cc-prev-title">
                                    
                                      {s?.title || "Section"}
                                    </div>
                                    <div className="cc-sub">
                                      {safeArray(s?.topics).length} lessons
                                    </div>
                                  </div>

                                  <HiChevronDown className="cc-chevron" />
                                </div>
                              );
                            })}
                          </div>
                        )} */}

                        <div className={`cc-panel ${headerOpen ? "is-open" : ""}`}>
                          <div className="cc-panel-inner">
                            {safeArray(h?.sections).length === 0 ? (
                              // <div className="text-secondary small">ยังไม่มี section</div>
                              <div className="text-secondary small">รอการประกาศ</div>
                            ) : (
                              safeArray(h.sections).map((s, si) => {
                                const secKey = `${hi}-${si}`;
                                const secOpen = curriculumExpandedAll || openSectionKey === secKey;

                                return (
                                  <div className="cc-sec" key={secKey}>
                                    <button
                                      type="button"
                                      className={`cc-secbtn ${secOpen ? "is-active" : ""}`}
                                      onClick={() => {
                                        setCurriculumExpandedAll(false);
                                        setOpenHeaderKey(headerKey);
                                        setOpenSectionKey(secOpen && !curriculumExpandedAll ? "" : secKey);
                                      }}
                                    >
                                      <div>
                                        <div className="cc-title" style={{ fontSize: 14 }}>
                                          {/* {si + 1}. {s?.title || "Section"} */}
                                          {s?.title || "Section"}
                                        </div>
                                        <div className="cc-sub">
                                          {safeArray(s?.topics).length} จำนวน EP
                                        </div>
                                      </div>

                                      <div className={`cc-chevron ${secOpen ? "is-open" : "is-closed"}`}>
                                        {secOpen ? <BsChevronDown /> : <BsChevronRight />}
                                      </div>
                                    </button>

                                    <div className={`cc-secbody ${secOpen ? "is-open" : ""}`}>
                                      <div className="cc-secbody-inner">
                                        {safeArray(s?.topics).length === 0 ? (
                                          <div className="text-secondary small">ยังไม่มีหัวข้อ</div>
                                        ) : (
                                          <ul className="cc-topic">
                                            {safeArray(s.topics).map((t, ti) => (
                                              <li key={ti}>
                                                <div className="d-flex align-items-center gap-2 min-w-0">
                                                  <span
                                                    style={{
                                                      width: 10,
                                                      height: 10,
                                                      borderRadius: "50%",
                                                      flex: "0 0 auto",
                                                      ...getLessonBulletStyle(t?.bulletStatus),
                                                    }}
                                                  />

                                                  <div className="min-w-0 w-100">
                                                    <div className="d-flex justify-content-between align-items-center gap-2">
                                                      <span className="text-break">
                                                        {t?.title || "-"}
                                                      </span>

                                                      {t?.releaseDateText ? (
                                                        // <span className="text-secondary small text-nowrap">
                                                        <span className="cc-lesson-date text-secondary small text-nowrap">
                                                          {t.releaseDateText}
                                                        </span>
                                                      ) : (
                                                        // <span className="text-secondary small">—</span>
                                                        <span className="cc-lesson-date text-secondary small">—</span>
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              </li>
                                            ))}
                                          </ul>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4">
                <h2 className="h5">ผู้สอน</h2>

                <div className="d-flex gap-3 align-items-start">
                  <img
                    src={
                      instructor.photo ||
                      "https://placehold.co/96x96/png?text=Instructor"
                    }
                    alt={instructor.name}
                    className="rounded-circle border"
                    style={{
                      width: 56,
                      height: 56,
                      objectFit: "cover",
                    }}
                  />

                  <div>
                    <div className="fw-semibold">{instructor.name}</div>
                    <div className="text-secondary">{instructor.bio}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-4 order-2 order-lg-2 course-detail-sticky-col">
              <div className="card app-card course-detail-sidecard">
                <div className="ratio ratio-16x9">
                  {youtubeVideoId ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${youtubeVideoId}?rel=0&modestbranding=1`}
                      title="แนะนำคอร์ส"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                      style={{ border: 0 }}
                    />
                  ) : course.imageCoverUrl ? (
                    <img
                      src={course.imageCoverUrl}
                      alt={course.title}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <div className="h-100 d-flex align-items-center justify-content-center text-secondary">
                      ไม่มีวิดีโอตัวอย่าง
                    </div>
                  )}
                </div>

                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="text-secondary">ราคา</div>
                    <div className="h4 mb-0">{formatCoursePrice(course)}</div>
                  </div>

                  <div className="d-grid gap-2">
                    <CoursePretestButton courseId={course.id} />
                    {hasGrantedAccess ? (
                      <CourseAccessGate
                        courseId={course.id}
                        onAllowed={() => navigate(`/learn/${course.id}`)}
                      >
                        {({ openGate, checking }) => (
                          <InstallmentCourseEntryGuard
                            course={courseForEntryGuard}
                            onAllowed={openGate}
                          >
                            {({ handleEnter }) => (
                              <button
                                className="btn btn-success"
                                onClick={handleEnter}
                                disabled={checking}
                              >
                                {checking ? "กำลังตรวจสอบ..." : "เริ่มเรียน"}
                              </button>
                            )}
                          </InstallmentCourseEntryGuard>
                        )}
                      </CourseAccessGate>
                    ) : (
                      <button className="btn btn-primary" onClick={onEnroll}>
                        สมัครเรียน
                      </button>
                    )}

                    {course?.courseType === "thaibar" && hasGrantedAccess ? (
                      <button className="btn btn-info text-white" onClick={onEnroll}>
                        สมัครเรียนเพิ่ม
                      </button>
                    ) : null}
                  </div>

                  {loadingEnroll ? (
                    <div className="small text-secondary mt-2">
                      <Spinner animation="border" size="sm" /> กำลังตรวจสอบสิทธิ...
                    </div>
                  ) : null}

                  <hr />

                  <div className="fw-semibold mb-2">สิ่งที่คุณจะได้รับ</div>
                  {receives.length === 0 ? (
                    <div className="text-secondary small">-</div>
                  ) : (
                    <ul className="mb-0">
                      {receives.map((t, i) => (
                        <li key={i} className="text-secondary">
                          {t}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* หมายเหตุ:
          ถ้ายังมี bullet เล็กซ้ำในรายการ lesson
          ให้ตรวจ CSS ว่ามีอันนี้ด้วย

          .cc-topic {
            margin: 0;
            padding-left: 0;
            list-style: none;
          }

          .cc-topic li {
            list-style: none;
          }
      */}
      {/* <div className="mobile-enroll-bar d-lg-none">
        {!hasGrantedAccess ? (
          <button className="btn btn-primary w-100" onClick={onEnroll}>
            สมัครเรียน
          </button>
        ) : course?.courseType === "thaibar" ? (
          <button className="btn btn-info w-100 text-white" onClick={onEnroll}>
            สมัครเรียนเพิ่ม
          </button>
        ) : (
          <button className="btn btn-success w-100" onClick={() => navigate(`/learn/${course.id}`)}>
            เริ่มเรียน
          </button>
        )}
      </div> */}
    </div>
  );
}
